import { useState } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import AccountGeneralInformation from './AccountGeneralInformation';
import SubaccountsTab from './SubaccountsTab';
import WalletBalancesTab from './WalletBalancesTab';
import { SidebarTab } from 'src/@types/types';
import BridgeTab from './BridgeTab';
import { CurrentSpotMarkets } from 'src/environmentVariables/EnvironmentVariables';
import {
	BigNum,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@drift-labs/sdk';
import useCurrentWalletCollateralBalance from 'src/hooks/useCurrentWalletCollateralBalance';
import useDriftClient from 'src/hooks/useDriftClient';
import UnderlinedTabs from 'src/components/TabbedNavigation/UnderlinedTabs';
import SidePanel from 'src/components/SidePanel';
import useDriftActions from 'src/hooks/useDriftActions';
import MarginAccountButton from 'src/components/MarginAccountButton';

export default function AccountSidebarModal() {
	const actions = useDriftActions();
	const isMobile = useIsMobileScreenSize();
	const defaultTab = useDriftStore(
		(s) => s.modalsProps.showAccountsSidebarModal?.defaultTab
	);
	const [selectedTab, setSelectedTab] = useState(
		defaultTab ?? SidebarTab.Subaccounts
	);
	const driftClient = useDriftClient();

	// Wallet states
	const wallet = useDriftStore((s) => s.wallet);
	const isMagicLinkWallet = wallet.isMagicAuth;
	const isMetamask = wallet.isMetamask;
	const isExternalWallet = isMagicLinkWallet || isMetamask;

	// Total Wallet Balances
	const balances = CurrentSpotMarkets.map(useCurrentWalletCollateralBalance);
	const prices = CurrentSpotMarkets.map(
		(bank) =>
			driftClient?.getOracleDataForSpotMarket(bank.marketIndex)?.price ?? ZERO
	);

	const isBalancesLoading = balances.some((balance) => balance[1]);
	const totalWalletQuoteBalances = balances.reduce((sum, balance, i) => {
		const oraclePrice = BigNum.from(prices[i], PRICE_PRECISION_EXP);
		const quoteValue = balance[0]
			.shiftTo(PRICE_PRECISION_EXP)
			.mul(oraclePrice)
			.shiftTo(QUOTE_PRECISION_EXP);

		return sum.add(quoteValue);
	}, BigNum.zero(QUOTE_PRECISION_EXP));

	const SIDEBAR_TABS = [
		{
			label: 'Subaccounts',
			value: SidebarTab.Subaccounts,
		},
	]
		.concat(
			isExternalWallet
				? [
						{
							label: 'Wallet',
							value: SidebarTab.Wallet,
						},
				  ]
				: []
		)
		.concat(
			isMobile
				? []
				: [
						{
							label: 'Bridge',
							value: SidebarTab.Bridge,
						},
						// {
						// 	title: 'Buy Crypto',
						// 	value: SidebarTab.BuyCrypto,
						// },
				  ]
		);

	const onClose = () => {
		actions.hideSidebarModalWithTimeout();
	};

	const renderTab = () => {
		switch (selectedTab) {
			case SidebarTab.Subaccounts:
			default:
				return (
					<SubaccountsTab
						onClose={onClose}
						isZeroWalletBalances={totalWalletQuoteBalances.eqZero()}
					/>
				);
			case SidebarTab.Wallet:
				return <WalletBalancesTab onClose={onClose} />;
			case SidebarTab.Bridge:
				return <BridgeTab onClose={onClose} />;
			// case SidebarTab.BuyCrypto:
			// 	return <BuyCryptoTab />;
		}
	};

	return (
		<SidePanel onClose={onClose} className="sm:w-[420px] sm:max-w-[420px]">
			<div className="flex flex-col bg-container-bg-selected">
				<AccountGeneralInformation
					onClose={onClose}
					setSelectedTab={setSelectedTab}
					isExternalWallet={isExternalWallet}
					isBalancesLoading={isBalancesLoading}
					totalWalletQuoteBalances={totalWalletQuoteBalances}
				/>

				<div className="w-full p-4 pt-1">
					<MarginAccountButton
						className="w-full text-center"
						highlight
						centerText
					/>
				</div>

				<UnderlinedTabs
					options={SIDEBAR_TABS}
					currentSelection={selectedTab}
					onChange={(newSelection: string) => {
						setSelectedTab(newSelection as SidebarTab);
					}}
					tabClassName={
						'grow text-center text-[14px] leading-[16px] items-center justify-center'
					}
					gradient
				/>
			</div>

			<div className="p-4 py-6 overflow-auto grow thin-scroll">
				{renderTab()}
			</div>
		</SidePanel>
	);
}
